/**
 * Licensing API client — GET/POST /api/licensing/{status,activate,refresh}.
 *
 * ApiService throws a plain `{ detail }` (not an axios error). Unwrap that
 * into `Error.message` so callers never re-derive from axios internals.
 */
import { ApiService } from '@/utils/api'

export interface LicenseStatus {
  state: 'trial' | 'active' | 'expiring' | 'grace' | 'read_only'
  plan: string
  customer: string | null
  max_users: number
  modules: string[]
  ai_calls_month: number
  until: string | null
  paid_until?: string | null
  perpetual: boolean
  updates_blocked: boolean
  reason: string
  request_status: 'pending' | 'licensed'
  install_id: string
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err
  const detail = (err as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail) return new Error(detail)
  return new Error('Request failed')
}

async function unwrap<T>(p: Promise<T>): Promise<T> {
  try {
    return await p
  } catch (err) {
    throw toError(err)
  }
}

export const licenseApi = {
  getStatus: (): Promise<LicenseStatus> =>
    unwrap(ApiService.get<LicenseStatus>('/licensing/status')),

  activate: (code: string): Promise<LicenseStatus> =>
    unwrap(ApiService.post<LicenseStatus>('/licensing/activate', { activation_code: code })),

  refresh: (): Promise<LicenseStatus> =>
    unwrap(ApiService.post<LicenseStatus>('/licensing/refresh')),
}
